//画像ブラウザ(editMode:false), 画像ブラウザ編集モード(editMode:true)
import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Split from "react-split";
import { Box, Button, Paper, Grid, Fab, IconButton, TextField } from '@material-ui/core';
import { List, ListItem, ListItemText, ListItemIcon } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import Draggable, { DraggableEvent } from 'react-draggable';

import { RootState } from '../redux/rootReducer';
import { setSnackbar } from '../redux/workBackendSlice';
import { updateUserCondition } from '../redux/userConditionSlice';
import { generateImageId, addImage, ImageDb } from '../redux/imageDbSlice';
import { AttributeKinds } from '../redux/cardAttributeModelIndex';
import { updateLatelyImage } from '../redux/latelyImageDbSlice';
import { AlbumDb, updateAlbum } from '../redux/albumDbSlice';
import { RelationUserAlbumDb, updateRelationUserAlbum } from '../redux/relationUserAlbumDbSlice';
import { addRelationUserImage } from '../redux/relationUserImageDbSlice';
import { RelationAlbumImageDb, addRelationAlbumImage, deleteRelationAlbumImage, updateRelationAlbumImage } from '../redux/relationAlbumImageDbSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useImageSize from '../hooks/useImageSize';
import useAllAlbumDbs from '../hooks/useAllAlbumDbs';
import useLatelyImages from '../hooks/useLatelyImages';
import useImagesInAlbum from '../hooks/useImagesInAlbum';
import useAllImages from '../hooks/useAllImages';
import useUserCondition from '../hooks/useUserCondition';
import useElementDimensions from '../hooks/useElementDimensions';

import { FullSizeImageAddType, Hash, deepCopy, getImgSize, makeLately } from '../general';
import DraggableSort from '../general/DraggableSort';
import AddAlbumDialog from '../components/AddAlbumDialog';
import DeleteAlbumDialog from '../components/DeleteAlbumDialog';
import DeleteImageDialog from '../components/DeleteImageDialog';

type FadeDialogTitleProps = {
  $isVisible: boolean;
}
const FadeDialogTitle = styled(DialogTitle)<FadeDialogTitleProps>`
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 200ms ease-out;
`
const EditButton = styled(Button)`
  position: absolute;
  right: 8px;
  top: 8px;
`
type LeftPanelProps = {
  $width: string;
}
const LeftPanel = styled.div<LeftPanelProps>`
  position: relative;
  width: ${props => props.$width};
  cursor: auto;
  z-index: 1;
`
type RightPanelProps = {
  $imageDragging: boolean;
}
const RightPanel = styled.div<RightPanelProps>`
  position: relative;
  width: inherit;
  height: 100%;
  cursor: auto;
  z-index: ${props => props.$imageDragging ? 2 : 0};
  overflow: hidden;
`
type ImageBasePaperProps = {
  $selected: boolean;
  $invalidTransition: boolean;
}
const ImageBasePaper = styled(Paper)<ImageBasePaperProps>`
  width: 200px;
  height: 120px;
  cursor: grab;
  border: ${props => props.$selected && '2px solid #1e88e5'};
  transition: ${props => props.$invalidTransition ? 'none' : undefined};
`
type AddImageFabProps = {
  $isVisible: boolean;
}
const AddImageFab = styled(Fab)<AddImageFabProps>`
  position: absolute;
  bottom: 8vh;
  right: 3vw;
  color: white;
  z-index: 999;
  background-color: #1e88e5;
  transform: ${props => props.$isVisible ? 'scale(1)' : 'scale(0)'};
  transition: transform 180ms;
  &:hover {
    background-color: #1976d2;
  }
`
const ImageBrowserDialogBox = styled(Box)`
  min-height: 90vh;
`
const ImageBrowserDialogActions = styled(DialogActions)`
  position: absolute;
  right: 0;
  bottom: 0;
`
type AlbumListItemProps = {
  $dragging: boolean;
  $invalidTransition: boolean;
}
const AlbumListItem = styled(ListItem)<AlbumListItemProps>`
  position: absolute;
  height: 72px;
  pointer-events: ${props => props.$dragging ? 'none' : undefined};
  z-index: ${props => props.$dragging ? 2 : 1};
  transition: ${props => (props.$dragging || props.$invalidTransition) ? undefined : '200ms ease-out'};
`
const GreyListItemIcon = styled(ListItemIcon)`
  color: #9e9e9e;
`
type AlbumNameWrapperProps = {
  $x: string;
  $editMode: boolean;
}
const AlbumNameWrapper = styled.div<AlbumNameWrapperProps>`
  position: sticky;
  margin-top: ${props => props.$editMode ? '0' : '-72px'};
  width: 64%;
  height: auto;
  background-color: white;
  opacity: ${props => props.$editMode ? 1 : 0};
  z-index: 3;
  transition: 300ms;
`
const AlbumNameTextField = styled(TextField)`
  width: 100%;
  margin: 8px 0 8px 8px;
`
type ImagesGridListWrapperProps = {
  $height?: number;
  elementDragging: boolean;
}
const ImagesGridListWrapper = styled.div<ImagesGridListWrapperProps>`
  position: relative;
  top: 0;
  width: 100%;
  height: ${props => props.$height ? `${props.$height}px` : '100%'};
  overflow-x: hidden;
  overflow-y: auto;
  z-index: ${props => props.elementDragging ? 2 : 0};
  transition: height 300ms;
`
type TileWrapperProps = {
  $dragging: boolean;
  $invalidTransition: boolean;
}
const TileWrapper = styled.div<TileWrapperProps>`
  position: absolute;
  width: 200px;
  height: 190px;
  pointer-events: ${props => props.$dragging ? 'none' : 'auto'};
  z-index: ${props => props.$dragging ? 5 : 4};
  transition: ${props => (props.$dragging || props.$invalidTransition) ? undefined : '300ms ease-out'};
`
type TileDivProps = {
  $invalidTransition: boolean;
  albumChanging: boolean;
}
const TileDiv = styled.div<TileDivProps>`
  opacity: ${props => props.albumChanging ? '0.5' : undefined};
  transform: ${props => props.albumChanging ? 'scale(0.7)' : undefined};
  transition: ${props => props.$invalidTransition ? undefined : '100ms ease-in'};
`
type EditMenuBoxProps = {
  $editMode: boolean;
  $dragging: boolean;
  $invalidTransition: boolean;
}
const EditMenuBox = styled(Box)<EditMenuBoxProps>`
  opacity: ${props => (props.$dragging || !props.$editMode) ? 0 : 1};
  pointer-events: ${props => props.$editMode ? undefined : 'none'};
  transition: ${props => props.$invalidTransition ? undefined : 'opacity 300ms'};
`
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
    },
    seleced: {
      border: '2px solid #1e88e5',
    },
    blue: {
      color: '#1e88e5',
    },
    grey: {
      color: '#9e9e9e',
    },
    split: {
      display: 'flex',
      height: '74vh',
      cursor: 'ew-resize',
      zIndex: 2,
      position: 'relative',
    },
  }),
)

type AlbumValueType = AlbumDb & { order: number }
export type ImageValueType = ImageDb & { order: number }
type ImageListHashType = { [key in string]: { ids: string[]; values: ImageValueType[]; } }
const albumListItemHeight = 72;
const dragTargetAlbumClass = 'album-list-item';
const dragHandleAlbumClass = 'album-drag-indicator';
const dragTargetImageClass = 'draggable-tile';
const dragHandleImageClass = 'handle-image';

type SimpleListProps = {
  albumIds: string[];
  albumList: AlbumValueType[];
  setAlbumList: (list: AlbumValueType[]) => void;
  onAlbumDelete?: (id: string) => void;
  selectingId: string;
  onSelect: (id: string) => void;
  editMode: boolean
  onAddAlbum: () => void
  draggingId?: string;
  setDraggingId: (id?: string) => void;
  changeAOrder: () => void;
  setInvalidImageTransition: (value: boolean) => void;
}
const SimpleList: React.FC<SimpleListProps> = (props) => {
  const { albumIds, albumList, setAlbumList, onAlbumDelete, selectingId, onSelect, editMode, onAddAlbum, draggingId, setDraggingId, changeAOrder, setInvalidImageTransition } = props;
  const classes = useStyles()
  const [invalidAlbumTransition, setInvalidAlbumTransition] = useState(false);
  const orderAdjust = editMode ? 1: 2;

  const onStop = () => {
    setInvalidAlbumTransition(true);
    setTimeout(() => setInvalidAlbumTransition(false), 300)
    setInvalidImageTransition(true);
    setTimeout(() => setInvalidImageTransition(false), 300)
    changeAOrder();
  }

  return (
    <Box style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', overflowY: 'auto' }}>
      <List component="nav" style={{ width: 'inherit', height: albumIds.length * albumListItemHeight }}>
        { editMode || (
          <Draggable disabled position={{x: 0, y: 0}}>
            <AlbumListItem button key={'lately'} selected={selectingId === 'lately'}
              onClick={() => onSelect('lately')}
              $dragging={false} $invalidTransition={invalidAlbumTransition}
            >
              <ListItemText primary="最近使った画像" key={'lately'} />
            </AlbumListItem>
          </Draggable>
        )}
        <Draggable disabled position={{x: 0, y: (orderAdjust - 1) * albumListItemHeight}}>
          <AlbumListItem button key={'allCustom'} selected={selectingId === 'allCustom'}
            onClick={() => onSelect('allCustom')}
            $dragging={false} $invalidTransition={invalidAlbumTransition}
          >
            <ListItemText primary="ユーザ登録画像" key={'allCustom'} style={{ marginLeft: editMode ? 56 : 0 }} />
          </AlbumListItem>
        </Draggable>
        { albumIds.map((id, ix) => {
          const album = albumList[ix];
          if (!album) return;
          return (
            <DraggableSort key={ix}
              draggableProps={{
                position: { x: 0, y: (album.order + orderAdjust) * albumListItemHeight },
                handle: `.${dragHandleAlbumClass}`,
                onStart: () => onSelect(id),
                onStop: () => onStop(),
                bounds: 'parent',
                axis: 'y',
                disabled: !editMode
              }}
              targetClass={dragTargetAlbumClass}
              id={id} ids={albumIds} values={albumList}
              setValues={setAlbumList}
              setDraggingId={setDraggingId}
            >
              <AlbumListItem button key={id} selected={selectingId === id}
                onClick={() => onSelect(id)}
                id={id} className={dragTargetAlbumClass}
                $dragging={id === draggingId}
                $invalidTransition={invalidAlbumTransition}
              >
                { editMode && <GreyListItemIcon className={dragHandleAlbumClass}><DragIndicatorIcon /></GreyListItemIcon> }
                <ListItemText primary={album.name} />
                { editMode &&
                  <ListItemIcon>
                    <IconButton onClick={() => onAlbumDelete && onAlbumDelete(id)}
                      // disabled={album.order === 0}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </ListItemIcon> }
              </AlbumListItem>
            </DraggableSort>
          );
        }) }
        { editMode && 
          <Draggable disabled position={{x: 0, y: (albumIds.length + 1) * albumListItemHeight}}>
            <AlbumListItem button key={'add'} onClick={() => onAddAlbum()}
              $dragging={false} $invalidTransition={invalidAlbumTransition}
            >
              <ListItemIcon className={classes.blue}><AddIcon /></ListItemIcon>
              <ListItemText primary="アルバムを作成する" className={classes.blue} />
            </AlbumListItem>
          </Draggable>
        }
      </List>
    </Box>
  )
}

type ImagesGridListProps = {
  albumId: string;
  imageIds: string[];
  imageValues: ImageValueType[];
  setImageValues: (values: ImageValueType[]) => void;
  selectingImageId: string;
  setSelectingImageId: (selectingImageId: string) => void;
  editMode: boolean;
  onAdd: (id: string) => void;
  onDelete: (id: string) => void;
  draggingId?: string;
  setDraggingId: (id?: string) => void;
  albumChangingId?: string;
  changeIOrder: (ev: DraggableEvent, id: string) => void;
  checkAlbumChanging: (ev: DraggableEvent, id: string) => void;
  invalidImageTransition: boolean;
  invalidEditBoxTransition: boolean;
  width?: number;
  height?: number;
}
const ImagesGridList: React.FC<ImagesGridListProps> = (props) => {
  const { albumId, imageIds, imageValues, selectingImageId, setSelectingImageId, editMode, onAdd, onDelete, draggingId, setDraggingId, albumChangingId, changeIOrder, setImageValues, checkAlbumChanging, invalidImageTransition, invalidEditBoxTransition, width, height } = props;
  const basicSize = { width: 200, height: 120 } as const;
  const basicRatio = basicSize.width / basicSize.height;
  const colWidth = basicSize.width + 2 + 16 * 2;// 画像の高さ + 枠の太さ + 左右マージン
  const colHeight = basicSize.height + 2 + 44 + 8 * 2;// 画像の高さ＋枠の太さ（1*2）＋カード名の高さ＋ボタンの高さ＋上下マージン
  const [listCols, setListCols] = useState<number>(width ? Math.trunc(width / colWidth) : 3);
  const [latelyIds, latelyValues] = useLatelyImages();
  const [allIds, allValues] = useAllImages();
  const ids = (!editMode && albumId === 'lately') ? latelyIds : albumId === 'allCustom' ? allIds : imageIds;
  const values = (!editMode && albumId === 'lately') ? latelyValues : albumId === 'allCustom' ? allValues : imageValues;

  useEffect(() => {
    if (!width) return;
    setListCols(Math.trunc(width / colWidth));
  }, [width])

  const imageDoubleClicked = (id: string) => {
    setSelectingImageId(id);
    if (editMode) return;
    onAdd(id);
  }
  const getSize = (width: number, height: number) => {
    const widthDividedHeight = width / height;
    if (widthDividedHeight > basicRatio) return { width: basicSize.width, height: height * basicSize.width / width };
    else return { width: width * basicSize.height / height, height: basicSize.height };
  }

  return (
    <ImagesGridListWrapper $height={height} elementDragging={Boolean(draggingId)}>
      <div style={{paddingTop: '8px', paddingLeft: '16px', height: colHeight * Math.trunc(ids.length / listCols)}}>
        { ids.map((id, ix) => {
          const image = values[ix];
          if (!image) return;
          const [dataWidth, dataHeight] = getImgSize(image.dataUrl);
          return (
            <DraggableSort key={ix}
              draggableProps={{
                position: { x: colWidth * (image.order % listCols), y: colHeight * Math.trunc(image.order / listCols) },
                handle: `.${dragHandleImageClass}`,
                onStop: (ev: DraggableEvent) => changeIOrder(ev, id),
                bounds: { left: undefined, top: undefined, right: undefined, bottom: undefined },
                disabled: !editMode
              }}
              targetClass={dragTargetImageClass}
              onDrag={(ev: DraggableEvent) => checkAlbumChanging(ev, id)}
              id={id} ids={ids} values={values}
              setValues={setImageValues}
              setDraggingId={setDraggingId}
              inactive={albumId === 'allCustom'}
            >
              <TileWrapper id={id} className={dragTargetImageClass}
                $dragging={id === draggingId} $invalidTransition={invalidImageTransition}
              >
                <TileDiv $invalidTransition={invalidImageTransition} albumChanging={id === albumChangingId}>
                  <ImageBasePaper variant='outlined'
                    id={id} className={dragHandleImageClass}
                    elevation={id === draggingId ? 8 : 0}
                    onClick={() => !editMode ? setSelectingImageId(id) : undefined}
                    onDoubleClick={() => imageDoubleClicked(id)}
                    $selected={!editMode && id === selectingImageId}
                    $invalidTransition={invalidImageTransition}
                  >
                    <Grid container justifyContent="center" alignItems="center">
                      <Grid item><img src={image.dataUrl} width={String(getSize(dataWidth, dataHeight).width)} height={String(getSize(dataWidth, dataHeight).height)} alt="" /></Grid>
                    </Grid>
                  </ImageBasePaper>
                  <EditMenuBox position="relative" left="152px"
                    $editMode={editMode} $dragging={id === draggingId}
                    $invalidTransition={invalidEditBoxTransition}
                  >
                    <IconButton onClick={() => onDelete(id)}>
                      { albumId === 'allCustom' ? <DeleteOutlinedIcon fontSize="small" /> : <RemoveCircleOutlineIcon fontSize="small" /> }
                    </IconButton>
                  </EditMenuBox>
                </TileDiv>
              </TileWrapper>
            </DraggableSort>
          );
        }) }
      </div>
    </ImagesGridListWrapper>
  );
}

type ImageBrowserProps = {
  open: boolean;
  onAddAttribute: (kind: AttributeKinds, image: FullSizeImageAddType) => void;
  onClose: () => void;
}
const ImageBrowserDialog: React.FC<ImageBrowserProps> = ({ open, onAddAttribute, onClose }) => {
  const dispatch = useDispatch();
  const albumDb = useSelector((state: RootState) => state.albumDb);
  const images = useSelector((state: RootState) => state.imageDb).images;
  const relationUserAlbums = useSelector((state: RootState) => state.relationUserAlbumDb).relationUserAlbums;
  const relationAlbumImages = useSelector((state: RootState) => state.relationAlbumImageDb).relationAlbumImages;
  const userId = useCurrentUserId();
  const [userConditionId, userCondition] = useUserCondition(userId);

  const classes = useStyles();

  const [albumIds, albumValues] = useAllAlbumDbs(userId);
  const albums: Hash<AlbumDb> = {};
  albumIds.forEach((id, ix) => albums[id] = albumValues[ix])
  const [albumList, setAlbumList] = useState<AlbumValueType[]>([]);
  const [imageListHash, setImageListHash] = useState<ImageListHashType>({});
  const [editMode, setEditMode] = useState<boolean>(false)
  const [selectingAlbumId, setSelectingAlbumId] = useState<string>('lately');
  const [selectingImageId, setSelectingImageId] = useState<string>('');
  const [imageWidth, imageHeight] = useImageSize(selectingImageId);
  const imagesLength = useImagesInAlbum(selectingAlbumId).length;
  const [addAlbumAction, setAddAlbumAction] = useState<boolean>(false);
  type DialogActionType = { show: boolean; id: string | undefined; }
  const [deleteImageAction, setDeleteImageAction] = useState<DialogActionType>({show: false, id: undefined});
  const [deleteAlbumAction, setDeleteAlbumAction] = useState<DialogActionType>({show: false, id: undefined});
  const [latelyIds, , latelyImagesId] = useLatelyImages();
  const [splitRatios, setSplitRatios] = useState<number[]>([25,75]);
  const [invalidEditBoxTransition, setInvalidEditBoxTransition] = useState(false);
  const [draggingId, setDraggingId] = useState<string>();
  const [invalidImageTransition, setInvalidImageTransition] = useState(false);
  const [albumChangingId, setAlbumChangingId] = useState<string>();
  const [splitUpdated, setSplitUpdated] = useState(true);
  const rightPanelId = 'image-browser-right-panel';
  const { width: rightPanelWidth, height: rightPanelHeight } = useElementDimensions(rightPanelId, splitUpdated);


  useEffect(() => {
    const newAlbumList: AlbumValueType[] = [];
    albumIds.forEach((id, ix) => {
      const album = albumValues[ix];
      const [ruaId, rua] = getRua(id);
      if (!ruaId || !rua) return;
      newAlbumList.push({ ...album, order: rua.order });
    })
    setAlbumList(newAlbumList);
  }, [albumDb, relationUserAlbums])

  useEffect(() => {
    const newImageList = deepCopy(imageListHash);
    albumIds.forEach(id => {
      const raiIds = Object.keys(relationAlbumImages).filter(raiId => relationAlbumImages[raiId].albumId === id);
      let changed = false;
      const reOrderSort = (ids: string[], values: Hash<RelationAlbumImageDb>): [string[], Hash<RelationAlbumImageDb>] => {
        const newIds = deepCopy(ids);
        const newValues = deepCopy(values);
        let reOrdered = false;
        newIds.sort((a, b) => {
          const aValue = values[a];
          const bValue = values[b];
          const orderGap = aValue.order - bValue.order;
          if (orderGap === 0) {
            newValues[b] = { ...bValue, order: bValue.order + 1 };
            reOrdered = true;
          }
          return orderGap;
        })
        if (reOrdered) {
          changed = true;
          return reOrderSort(newIds, newValues);
        }
        if (changed) newIds.forEach(targetId => dispatch(updateRelationAlbumImage({ id: targetId, ...newValues[targetId] })))
        return [newIds, newValues];
      }
      const [sortedIds] = reOrderSort(raiIds, relationAlbumImages);
      const imageIds = sortedIds.map(raiId => relationAlbumImages[raiId].imageId);
      const imageValues = imageIds.map((imageId, imageIx) => {
        return { ...images[imageId], order: relationAlbumImages[sortedIds[imageIx]].order };
      })
      newImageList[id] = { ids: imageIds, values: imageValues };
    })
    setImageListHash(newImageList);
  }, [])

  useEffect(() => {
    const newImageList = deepCopy(imageListHash);
    albumIds.forEach(id => {
      const raiIds = Object.keys(relationAlbumImages).filter(raiId => relationAlbumImages[raiId].albumId === id);
      raiIds.sort((a, b) => {
        const aValue = relationAlbumImages[a];
        const bValue = relationAlbumImages[b];
        const orderGap = aValue.order - bValue.order;
        return orderGap;
      })
      const imageIds = raiIds.map(raiId => relationAlbumImages[raiId].imageId);
      const imageValues = imageIds.map((imageId, imageIx) => {
        return { ...images[imageId], order: relationAlbumImages[raiIds[imageIx]].order };
      })
      newImageList[id] = { ids: imageIds, values: imageValues };
    })
    setImageListHash(newImageList);
  }, [images, relationAlbumImages])

  const toggleMode = () => {
    setSelectingImageId('');
    setEditMode(!editMode);
  }
  const getRua = (albumId: string): [string | undefined, RelationUserAlbumDb | undefined] => {
    const id = Object.keys(relationUserAlbums).find(id => {
      const target = relationUserAlbums[id];
      return target.albumId === albumId && target.userId === userId;
    })
    if (!id) return [undefined, undefined];
    const value = relationUserAlbums[id];
    return [id, value];
  }
  const getRai = (imageId: string, albumId: string): [string | undefined, RelationAlbumImageDb | undefined] => {
    const id = Object.keys(relationAlbumImages).find(id => {
      const target = relationAlbumImages[id];
      if (albumId === 'allCustom') return target.imageId === imageId;
      else return target.albumId === albumId && target.imageId === imageId;
    })
    if (!id) return [undefined, undefined];
    const value = relationAlbumImages[id];
    return [id, value];
  }
  const openImageFileSelector = () => {
    const selector = document.getElementById('image-file-selector');
    if (!selector) return;
    selector.click();
  }
  const getImageFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.files) return;
    const file = ev.target.files[0];
    if (!file) return;
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const dataUrlBase64 = reader.result;
      const imageId = generateImageId();
      if (selectingAlbumId === 'lately') return;
      dispatch(addImage({id: imageId, authorId: userId || 'no user', dataUrl: dataUrlBase64}));
      // add relation between user and image
      dispatch(addRelationUserImage({userId: userId || 'no user', imageId: imageId}));
      // add relation between album and image
      dispatch(addRelationAlbumImage({albumId: selectingAlbumId, imageId: imageId, order: imagesLength}));
    }
  }
  const exitDialog = () => {
    setEditMode(false);
    setSelectingImageId('');
  }
  const dicision = () => {
    onAddAttribute('image', { id: selectingImageId, width: imageWidth, height: imageHeight });

    const newLatelyIds = makeLately(latelyIds, selectingImageId, 'add');
    dispatch(updateLatelyImage({id: latelyImagesId, imageIds: newLatelyIds}));

    onClose();
  }
  const albumSelect = (id: string) => {
    setInvalidImageTransition(true);
    setTimeout(() => setInvalidImageTransition(false), 300)
    setInvalidEditBoxTransition(true);
    setTimeout(() => setInvalidEditBoxTransition(false), 300)
    setSelectingAlbumId(id);
  }
  const changeAlbumName = (ev: any) => {
    if (selectingAlbumId === 'lately') return;
    const newName = ev.target.value;
    dispatch(updateAlbum({ id: selectingAlbumId, name: newName }));
  }
  const getAlbumId = (element: HTMLElement): string | undefined => {
    const elementClass = element.className;
    if (!element || typeof elementClass !== 'string') return;
    else if (elementClass.includes(dragTargetAlbumClass)) return element.id;
    else if (element.parentElement) return getAlbumId(element.parentElement);
    else return;
  }
  const changeAOrder = () => {
    const ids = albumIds;
    const list = albumList;

    ids.forEach((targetId, targetIx) => {
      const value = list[targetIx];
      const [ruaId, rua] = getRua(targetId) || [undefined, undefined];
      if (!ruaId || !rua) return;
      if (value.order === rua.order) return;
      dispatch(updateRelationUserAlbum({ id: ruaId, order: value.order }));
    })
  }
  const changeIOrder = (ev: DraggableEvent, id: string) => {
    setAlbumChangingId(undefined);

    const target = ev.target as HTMLElement;
    const destinationAlbumId = getAlbumId(target);

    const [raiId, rai] = getRai(id, selectingAlbumId);
    if (!raiId || !rai) {
      if (destinationAlbumId) {
        const destination = imageListHash[destinationAlbumId];
        dispatch(addRelationAlbumImage({ albumId: destinationAlbumId, imageId: id, order: destination.ids.length }));
        albumSelect(destinationAlbumId);
      }
      return;
    }

    const imageValues = imageListHash[rai.albumId];
    if (!imageValues) return;
  
    const ids = imageValues.ids;
    const list = imageValues.values;

    if (destinationAlbumId && destinationAlbumId !== rai.albumId) {
      const destination = imageListHash[destinationAlbumId];
      ids.forEach(targetId => {
        if (targetId === id) return;
        const [targetRaiId, targetRai] = getRai(targetId, selectingAlbumId);
        if (!targetRaiId || !targetRai) return;
        else if (targetRai.order > rai.order) dispatch(updateRelationAlbumImage({ id: targetRaiId, order: targetRai.order - 1 }));
      })
      // ここから下をforEachより前に持っていけば見かけ上は早くなるが、処理が終わる前に次の操作が入っておかしくなる危険がある。リカバリーのコードを用意するなら良いかも。
      dispatch(updateRelationAlbumImage({ id: raiId, albumId: destinationAlbumId, order: destination.ids.length }));
      albumSelect(destinationAlbumId);
    } else if (selectingAlbumId !== 'allCustom') {
      ids.forEach((targetId, targetIx) => {
        const value = list[targetIx];
        const [targetRaiId, targetRai] = getRai(targetId, selectingAlbumId);
        if (!targetRaiId || !targetRai || value.order === targetRai.order) return;
        dispatch(updateRelationAlbumImage({ id: targetRaiId, order: value.order }));
      })
    }
  }
  const setImageValues = (values: ImageValueType[]) => {
    const newLists = deepCopy(imageListHash);
    if (!newLists[selectingAlbumId]) return;
    newLists[selectingAlbumId].values = values;
    setImageListHash(newLists);
  }
  const checkAlbumChanging = (ev: DraggableEvent, id: string) => {
    const target = ev.target as HTMLElement;
    const destinationAlbumId = getAlbumId(target);
    const [, rai] = getRai(id, selectingAlbumId);
    if (destinationAlbumId) {
      if (!rai) setAlbumChangingId(id);
      else if (destinationAlbumId !== rai.albumId) setAlbumChangingId(id);
    }
    else setAlbumChangingId(undefined);
  }
  const updateAOrder = (albumId: string) => {
    const [targetId, target] = getRua(albumId);
    if (!targetId || !target) return;
    const ids = albumIds;
    ids.forEach(id => {
      const [ruaId, rua] = getRua(id);
      if (!ruaId || !rua || ruaId === targetId) return;
      else if (rua.order > target.order) dispatch(updateRelationUserAlbum({ id: ruaId, order: rua.order - 1 }));
    })
  }
  const updateIOrder = (imageId: string) => {
    if (!selectingAlbumId) return;
    const [targetId, target] = getRai(imageId, selectingAlbumId);
    const imageValues = imageListHash[selectingAlbumId];
    if (!targetId || !target || !imageValues) return;
    imageValues.ids.forEach(id => {
      const [raiId, rai] = getRai(id, selectingAlbumId);
      if (!raiId || !rai || raiId === targetId) return;
      else if (rai.order > target.order) dispatch(updateRelationAlbumImage({ id: raiId, order: rai.order - 1 }));
    })
  }
  const removeOrDeleteImage = (imageId: string) => {
    if (selectingAlbumId === 'allCustom') setDeleteImageAction({ show: true, id: imageId });
    else {
      const [raiId] = getRai(imageId, selectingAlbumId);
      if (!raiId) return;
      updateIOrder(imageId);
      dispatch(deleteRelationAlbumImage({ id: raiId }));
      dispatch(setSnackbar({ open: true, message: '画像をアルバムから取り除きました。ユーザ登録画像から使用できます。' }));
    }
  }
  const dragSplit = (sizes: number[]) => {
    if (!userConditionId || !userCondition) return;
    setSplitUpdated(false);
    const newRatio = deepCopy(userCondition.splitRatios);
    newRatio.imageBrowser = sizes;
    dispatch(updateUserCondition({ id: userConditionId, splitRatios: newRatio }));
    setSplitRatios(newRatio.imageBrowser);
    setTimeout(() => setSplitUpdated(true), 1)
  }

  useEffect(() => {
    if (!userCondition) return;
    setSplitRatios(userCondition.splitRatios.imageBrowser);
  }, [])

  return (
    <>
      <Dialog open={open} onClose={onClose} onExited={exitDialog} maxWidth="xl" fullWidth
        PaperProps={{ style: { overflow: 'hidden' } }}
      >
        <FadeDialogTitle $isVisible={!editMode}>画像を選択します</FadeDialogTitle>
        <FadeDialogTitle $isVisible={editMode} style={{position: 'absolute'}}>選択した画像を編集します</FadeDialogTitle>
        <ImageBrowserDialogBox>
          <EditButton color='primary' onClick={() => toggleMode()} size="large">{ editMode ? '完了' : '編集' }</EditButton> 
          <DialogContent>
            <div style={{width: '100%', height: '74vh', marginBottom: 58.25}}>
              <Split
                  minSize={250}
                  expandToMin={false}
                  gutterSize={5}
                  gutterAlign="center"
                  snapOffset={30}
                  dragInterval={1}
                  direction="horizontal"
                  cursor="col-resize"
                  sizes={[Number.isFinite(splitRatios[0]) ? splitRatios[0] : 25, Number.isFinite(splitRatios[1]) ? splitRatios[1] : 75]}
                  onDrag={(sizes: number[]) => dragSplit(sizes)}
                  className={classes.split}
              >
                  <LeftPanel $width={`${splitRatios[0]}%`}>
                    <SimpleList
                      albumIds={albumIds} albumList={albumList} setAlbumList={setAlbumList}
                      onAlbumDelete={(id) => setDeleteAlbumAction({show: true, id: id})} 
                      selectingId={selectingAlbumId} onSelect={(id) => albumSelect(id)}
                      editMode={editMode}
                      onAddAlbum={() => setAddAlbumAction(true)}
                      draggingId={draggingId} setDraggingId={setDraggingId}
                      changeAOrder={changeAOrder}
                      setInvalidImageTransition={setInvalidImageTransition}
                    />
                  </LeftPanel>
                  <RightPanel id={rightPanelId} $imageDragging={draggingId !== undefined && draggingId.substring(0, 2) === 'i:'} style={{ width: `${splitRatios[1]}%` }}>
                    { (selectingAlbumId !== 'lately' && albums[selectingAlbumId]) &&
                      <AlbumNameWrapper $x={`${splitRatios[0] + 1}%`} $editMode={editMode}>
                        <AlbumNameTextField label="アルバム名" variant="outlined" value={albums[selectingAlbumId].name}
                          onChange={changeAlbumName}
                          disabled={!editMode}
                        />
                      </AlbumNameWrapper>
                    }
                    <ImagesGridList
                      albumId={selectingAlbumId}
                      imageIds={imageListHash[selectingAlbumId] ? imageListHash[selectingAlbumId].ids : []}
                      imageValues={imageListHash[selectingAlbumId] ? imageListHash[selectingAlbumId].values : []}
                      selectingImageId={selectingImageId}
                      setSelectingImageId={setSelectingImageId}
                      editMode={editMode}
                      onAdd={() => dicision()}
                      onDelete={id => removeOrDeleteImage(id)}
                      draggingId={draggingId} setDraggingId={setDraggingId}
                      albumChangingId={albumChangingId}
                      changeIOrder={changeIOrder}
                      setImageValues={setImageValues}
                      checkAlbumChanging={checkAlbumChanging}
                      invalidImageTransition={invalidImageTransition}
                      invalidEditBoxTransition={invalidEditBoxTransition}
                      width={rightPanelWidth} height={(editMode && rightPanelHeight) ? rightPanelHeight - 72 : rightPanelHeight}
                    />
                  </RightPanel>
              </Split>
            </div>
          </DialogContent>
          { editMode || (
            <ImageBrowserDialogActions>
              <Button variant='text' color='primary' onClick={onClose}>
                キャンセル
              </Button>
              <Button variant='text' color='primary' onClick={() => dicision()}>
                選択した画像を挿入する
              </Button>
            </ImageBrowserDialogActions>
          )}
          <AddImageFab id="image-file-selector-button"
            onClick={openImageFileSelector}
            $isVisible={Boolean(editMode && selectingAlbumId !== 'lately' && albums[selectingAlbumId])}
          >
            <input type="file" accept="image/png, image/jpeg" id="image-file-selector" onChange={getImageFile} style={{display: 'none'}} />
            <AddIcon />
          </AddImageFab>
          </ImageBrowserDialogBox>
      </Dialog>
      <AddAlbumDialog open={addAlbumAction} 
        onClose={() => {setAddAlbumAction(false)}}
      />
      <DeleteAlbumDialog open={deleteAlbumAction.show} 
        onClose={() => {setDeleteAlbumAction({show: false, id: undefined})}}
        id={deleteAlbumAction.id}
        updateOrder={updateAOrder}
        updateSelection={() => {
          if (!deleteAlbumAction.id) return;
          const ix = albumIds.indexOf(deleteAlbumAction.id);
          if (ix >= 1) albumSelect(albumIds[ix - 1]);
          else if (ix === 0) albumSelect(albumIds[1]);
          else albumSelect(albumIds[0]);
        }}
      />
      <DeleteImageDialog open={deleteImageAction.show}
        onClose={() => {setDeleteImageAction({show: false, id: undefined})}}
        imageId={deleteImageAction.id}
        updateOrder={updateIOrder}
      />
    </>
  )
}

export default ImageBrowserDialog;
